import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import ProjectDisplay from "./ProjectDisplay";
import { User, UserProject } from "../Profile";
import { MonthPicker } from "../MonthPicker";
import { Checkbox } from "@mui/material";
import { toast } from "react-toastify";

type ProjectsProps = {
  handleClick: () => void;
};

export const Projects: React.FC<ProjectsProps> = ({ handleClick }) => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [personal, setPersonal] = useState(false);
  const [organization, setOrganization] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [failedSubmit, setFailedSubmit] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);

  const user = useSelector((state: AppState) => state.user); // get the current user object from the Redux store
  const [projects, setProjects] = useState<UserProject[]>(
    user.projects == undefined ? [] : user.projects
  );
  const [numProjects, setNumProjects] = useState(
    user.projects == undefined ? 0 : user.projects.length
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const updateState = (projs: UserProject[]) => {
    const updatedUser: User = {
      ...user, // create a new object with all existing properties of the user object
      projects: projs,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  const updateUserData = () => {
    if (
      name.length === 0 ||
      description.length === 0 ||
      (!personal && organization.length === 0)
    ) {
      toast.error("Please complete all required fields.");
      setFailedSubmit(true);
      return;
    }

    setFailedSubmit(false);

    const proj: UserProject = {
      name: name,
      personal: personal,
      organization: organization,
      start_time: startTime,
      end_time: endTime,
      description: description,
      id: numProjects,
    };

    setNumProjects(numProjects + 1);

    const projs = projects.concat(proj);

    setProjects(projs);
    updateState(projs);

    setName("");
    setPersonal(false);
    setOrganization("");
    setDescription("");
  };

  const removeProject = (index: number) => {
    const projs = projects.filter((proj) => proj.id != index);
    setProjects(projs);
    updateState(projs);
  };

  const handleNameInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setName(event.currentTarget.value);
  };

  const handleOrganizationInput = (
    event: React.FormEvent<HTMLTextAreaElement>
  ) => {
    setOrganization(event.currentTarget.value);
  };

  const handleDescription = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setDescription(event.currentTarget.value);
  };

  return (
    <div>
      <p className="text-light mb-10 pl-4 text-2xl font-bold lg:pl-0 lg:text-3xl xl:text-4xl">
        <span className="text-orange">Projects</span> and{" "}
        <span className="text-blue">Clubs</span>
      </p>

      {isOverlayOpen && (
        <div className="overlay">
          <div className="agreement-content w-1/2">
            <h2 className="text-center font-code md:text-lg lg:text-xl xl:text-2xl ">
              You have a partial project/club experience entry. Are you sure you
              want to move on?
            </h2>

            <div className="flex w-full flex-row pt-4 lg:pt-6">
              <button
                className="start-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => handleClick()}
              >
                Yes, I&apos;m sure
              </button>
              <button
                className="login-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => setIsOverlayOpen(false)}
              >
                Nevermind
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col space-x-10 md:flex-row">
        <div className="ml-auto hidden h-full w-full overflow-y-scroll rounded-md bg-[#1E1E1E] p-2 md:block md:max-h-[40rem] md:w-1/4">
          {projects.map((proj, index) => {
            const deleteIndex = () => {
              removeProject(proj.id);
            };

            return (
              <ProjectDisplay
                key={index}
                name={proj.name}
                personal={proj.personal}
                organization={proj.organization}
                start_time={proj.start_time}
                end_time={proj.end_time}
                index={index + 1}
                deleteRow={deleteIndex}
              />
            );
          })}

          {projects.length === 0 && (
            <h1 className="mt-20 h-32 font-code text-lg font-bold text-[#9CA3AF] lg:text-xl xl:text-2xl">
              No projects added...
            </h1>
          )}
        </div>

        <div className="w-4/5">
          <div className="flex flex-col md:flex-row">
            <div className="inline-block w-full md:w-1/2">
              <p className="text-orange pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                NAME OF PROJECT/CLUB <span className="text-red-500">*</span>
              </p>
              <textarea
                value={name}
                onInput={handleNameInput}
                rows={1}
                className={`answer-box mt-3 max-h-min w-full overflow-y-hidden p-5 text-2xl ${
                  failedSubmit ? "border-2 border-red-600" : ""
                }`}
                ref={textareaRef}
                placeholder="Robotic Arm Build"
              />
            </div>

            <div className="ml-auto inline-block w-full md:w-2/5">
              <p className="text-orange flex flex-row pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                ORGANIZATION{" "}
                <span
                  className={`text-red-500 ${
                    personal ? "hidden" : "flex pl-2"
                  }`}
                >
                  {" "}
                  *
                </span>
              </p>
              <textarea
                value={organization}
                onInput={handleOrganizationInput}
                disabled={personal}
                rows={1}
                className={`resize-none rounded-xl text-white ${
                  personal ? "bg-[#1d1c1c]" : "bg-[#333333]"
                } mt-3 w-full overflow-y-hidden p-5 text-2xl ${
                  failedSubmit && !personal ? "border-2 border-red-600" : ""
                }`}
                ref={textareaRef}
                placeholder="Rocket Club"
              />
              <div className="flex flex-row">
                <Checkbox
                  sx={{
                    color: "#F5F5F5",
                  }}
                  checked={personal}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setPersonal(event.target.checked)
                  }
                />
                <p className="text-light mt-2 font-code">
                  I did this by myself
                </p>
              </div>
            </div>
          </div>

          <div className="mb-5 flex flex-col md:flex-row"></div>
          <div className="flex flex-col md:flex-row">
            <div className="inline-block w-full md:w-1/2">
              <p className="text-orange pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                START DATE
              </p>
              <MonthPicker handleInput={setStartTime} disabled={false} />
            </div>
            <div className="ml-auto inline-block w-full md:w-2/5">
              <p className="text-blue pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                END DATE
              </p>
              <MonthPicker handleInput={setEndTime} disabled={false} />
            </div>
          </div>

          <div className="flex flex-col md:flex-row">
            <textarea
              value={description}
              rows={5}
              onInput={handleDescription}
              className={`answer-box mt-3 max-h-min w-full overflow-y-hidden p-5 text-2xl ${
                failedSubmit ? "border-2 border-red-600" : ""
              }`}
              ref={textareaRef}
              placeholder="A few sentences about what you did, in plain English. Use numbers if possible."
            />
          </div>

          <div className="flex flex-row space-x-10">
            <button
              className="start-button mt-5 px-10 py-2 text-lg font-bold md:py-4 md:text-xl lg:px-16 lg:text-3xl"
              onClick={updateUserData}
            >
              Add
            </button>
            <button
              className="login-button mt-5 px-10 py-2 text-lg font-bold md:py-4 md:text-xl lg:px-16 lg:text-3xl"
              onClick={() => {
                if (name.length > 0 || description.length > 0) {
                  setIsOverlayOpen(true);
                } else {
                  handleClick();
                }
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
